import { useEffect, useState } from 'react';
import { useClientStore } from '@core/client/useClientStore';
import { useSiteStore } from '@core/site/useSiteStore';
import type { Invite, User } from '@core/generated/gql-types';

type UseClientUsersData = {
  users: User[];
  invites: Invite[];
  totalClientSites: number;
  isLoading: boolean;
  refetchInvites: () => void;
  revokeInvite: (inviteId: string) => Promise<void>;
};

export const useClientUsersData = (
  clientId: string | undefined,
): UseClientUsersData => {
  const {
    users,
    invites,
    fetchUsersByClient,
    fetchInvitesForClient,
    revokeInvite,
  } = useClientStore();
  const { fetchSitesForClient, getSitesForClient } = useSiteStore();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!clientId) return;

      setIsLoading(true);
      try {
        // Fetch both users and invites in parallel
        await Promise.all([
          fetchUsersByClient(clientId),
          fetchInvitesForClient(clientId),
          fetchSitesForClient(clientId),
        ]);
      } catch (error) {
        console.error('Error fetching client users data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [
    clientId,
    fetchUsersByClient,
    fetchInvitesForClient,
    fetchSitesForClient,
    users.length,
  ]);

  const totalClientSites = clientId ? getSitesForClient(clientId).length : 0;

  const refetchInvites = () => {
    if (clientId) {
      fetchInvitesForClient(clientId);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    await revokeInvite(inviteId);
  };

  return {
    users,
    invites,
    totalClientSites,
    isLoading,
    refetchInvites,
    revokeInvite: handleRevokeInvite,
  };
};
